import type { TraceSpan } from "@zworks/console-contracts";
import { formatCredits, formatDurationMs, formatMicroUSD, formatTokens } from "@/lib/format";

export { formatCredits, formatDurationMs, formatMicroUSD, formatTokens };

export interface HierarchicalSpan {
  span: TraceSpan;
  depth: number;
  children: HierarchicalSpan[];
  hasChildren: boolean;
}

/**
 * Builds a hierarchical tree from a flat list of spans by matching parentSpanId.
 * Preserves parent-child ordering and determines nesting depth.
 */
export function buildSpanHierarchy(spans: TraceSpan[]): HierarchicalSpan[] {
  const spanMap = new Map<string, { span: TraceSpan; children: TraceSpan[] }>();
  const rootSpans: TraceSpan[] = [];

  for (const span of spans) {
    spanMap.set(span.spanId, { span, children: [] });
  }

  for (const span of spans) {
    if (span.parentSpanId && spanMap.has(span.parentSpanId)) {
      spanMap.get(span.parentSpanId)!.children.push(span);
    } else {
      rootSpans.push(span);
    }
  }

  function toHierarchical(span: TraceSpan, depth = 0): HierarchicalSpan {
    const entry = spanMap.get(span.spanId);
    const childSpans = entry ? entry.children : [];
    const children = childSpans.map((child) => toHierarchical(child, depth + 1));
    return {
      span,
      depth,
      children,
      hasChildren: children.length > 0,
    };
  }

  return rootSpans.map((root) => toHierarchical(root, 0));
}

/**
 * Flattens the hierarchical tree respecting collapsed nodes.
 */
export function flattenVisibleHierarchy(
  tree: HierarchicalSpan[],
  collapsedIds: Set<string>,
): HierarchicalSpan[] {
  const visible: HierarchicalSpan[] = [];

  function traverse(nodes: HierarchicalSpan[]) {
    for (const node of nodes) {
      visible.push(node);
      if (!collapsedIds.has(node.span.spanId) && node.children.length > 0) {
        traverse(node.children);
      }
    }
  }

  traverse(tree);
  return visible;
}
